<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);

// Only the plain-<div role="button"> case (no `to`) needs a manual
// Enter/Space activation. When `to` is set, this renders as a real
// <router-link> which already navigates on Enter natively — calling
// preventDefault() unconditionally here would swallow that keypress
// without emitting 'toggle' either, silently breaking keyboard nav.
const handleKeydownToggle = event => {
  if (props.to) return;
  event.preventDefault();
  emit('toggle');
};
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-1.5 py-1 rounded-lg h-8 min-w-0 transition-[background-color,box-shadow,color] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :tabindex="to ? undefined : 0"
    :aria-expanded="expandable ? isExpanded : undefined"
    :aria-current="isActive ? 'page' : undefined"
    :class="{
      'text-n-slate-12 font-medium bg-n-solid-2 shadow ring-1 ring-inset ring-n-slate-6 hover:bg-n-solid-3 hover:shadow-md hover:ring-n-slate-7':
        isActive && !hasActiveChild,
      'text-n-slate-12 font-medium hover:bg-n-alpha-2 hover:shadow-sm hover:ring-1 hover:ring-inset hover:ring-n-slate-6':
        hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2 hover:shadow-sm hover:ring-1 hover:ring-inset hover:ring-n-slate-6 hover:text-n-slate-12':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
    @keydown.enter="handleKeydownToggle"
    @keydown.space="handleKeydownToggle"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon
        v-if="icon"
        :icon="icon"
        class="size-4"
        :class="{ 'text-n-blue-11': isActive || hasActiveChild }"
      />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="inline-grid h-5 min-w-5 place-items-center rounded-full bg-n-slate-4 px-1 text-xxs font-medium leading-3 text-n-slate-12 dark:bg-n-slate-5 flex-shrink-0"
      >
        {{ count }}
      </span>
    </div>
    <!-- [metrica] Always rendered when expandable (not just once already
         open) and swaps direction with isExpanded — matches
         SidebarGroupSeparator.vue's own chevron convention. Previously this
         only appeared AFTER the group was expanded, so a collapsed group
         (9 of 11 top-level items) looked pixel-identical to a plain,
         non-expandable link (Metrica Scripts, Assinatura) until clicked —
         no visual cue distinguished "navigates" from "opens a submenu". -->
    <span
      v-if="expandable"
      class="size-3 flex-shrink-0"
      :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
